import { useSetAtom } from 'jotai';

import { assistantFileConfirmUploadMutationAtom } from '../../../_graphql/mutationAssistantFileConfirmUpload';
import { assistantFileCreateMutationAtom } from '../../../_graphql/mutationAssistantFileCreate';
import {
  $addAttachment,
  $removeAttachment,
  $updateAttachment,
} from '../../../_state/chatInput.state';
import type { PendingAttachment } from '../../../types';

import { useIdentity } from '~/shared/hooks/useIdentity';
import { generateUniqueFileName } from '~/shared/utils/fileHelpers';
import {
  getFileMimeType,
  uploadFileToS3,
  validateFile,
} from '~/shared/utils/fileUpload';

const $createFileMutation = assistantFileCreateMutationAtom({});
const $confirmUploadMutation = assistantFileConfirmUploadMutationAtom({});

export function useAssistantFileUpload() {
  const { user } = useIdentity();
  const addAttachment = useSetAtom($addAttachment);
  const updateAttachment = useSetAtom($updateAttachment);
  const removeAttachment = useSetAtom($removeAttachment);
  const createFile = useSetAtom($createFileMutation);
  const confirmUpload = useSetAtom($confirmUploadMutation);

  const uploadFile = async (file: File) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      const errorAttachment: PendingAttachment = {
        id: `att-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        status: 'error',
        progress: 0,
        error: validation.error,
      };
      addAttachment(errorAttachment);
      return;
    }

    const attachmentId = `att-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const attachment: PendingAttachment = {
      id: attachmentId,
      file,
      status: 'uploading',
      progress: 0,
    };
    addAttachment(attachment);

    try {
      const mimeType = getFileMimeType(file);
      const uniqueFileName = generateUniqueFileName(file.name);
      const result = await createFile({
        fileName: uniqueFileName,
        fileSize: file.size,
        mimeType,
      });

      const fileRecord = result?.filesCreateFile;
      if (!fileRecord?.uploadUrl || !fileRecord?.file?.id) {
        throw new Error('Failed to create file record');
      }

      const fileId = fileRecord.file.id;
      updateAttachment({ id: attachmentId, fileId });

      await uploadFileToS3(file, fileRecord.uploadUrl, {
        onProgress: (progress) => {
          updateAttachment({ id: attachmentId, progress: progress.percentage });
        },
        fileId,
        uploaderId: user?.id || 'unknown',
      });

      await confirmUpload({ fileId });

      updateAttachment({
        id: attachmentId,
        status: 'confirmed',
        progress: 100,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload failed';
      updateAttachment({
        id: attachmentId,
        status: 'error',
        error: message,
      });
    }
  };

  const uploadFiles = (files: FileList | File[]) => {
    Array.from(files).forEach(uploadFile);
  };

  return { uploadFile, uploadFiles, removeAttachment };
}
